/**
 * 테이블 공통 유틸리티
 * - 셀 위치 계산, 하이라이트, 히트 테스트, 생성, 행/열 조작, 경계선 리사이즈
 */
import * as fabric from 'fabric';
import { DEFAULT_TEXT_INSET, TABLE_MIN_ROW_HEIGHT } from '../constants/editorConstants';

/**
 * Fabric.js FitContentLayout 억제용 NOOP LayoutManager
 * - Group.add()/remove()가 자식 위치를 오염시키는 것을 방지
 */
export const NOOP_LAYOUT_MANAGER = {
  performLayout() {},
  toObject() { return {}; },
  subscribe() {},
  unsubscribe() {},
  subscribeTargets() {},
  unsubscribeTargets() {},
  attachHandlers() { return []; },
  _subscriptions: new Map(),
};

/**
 * 그룹의 기존 LayoutManager 이벤트 구독을 해제하고 NOOP으로 교체
 * Fabric.js v7에서 FitContentLayout은 자식의 changed/modified 이벤트에 구독하므로
 * 단순 교체만으로는 기존 구독이 남아 있어 layout 재계산이 계속 발생함
 */
export const setNoopLayoutManager = (group) => {
  if (group.layoutManager?.unsubscribeTargets) {
    group.layoutManager.unsubscribeTargets({
      targets: group.getObjects ? group.getObjects() : [],
      target: group,
    });
  }
  group.layoutManager = NOOP_LAYOUT_MANAGER;
};

/**
 * 셀의 4방향 테두리 Line 객체 생성 헬퍼
 */
const createCellBorderLines = (row, col, pos, borders, fallbackColor = '#000000', fallbackWidth = 1) => {
  const x1 = pos.localCx - pos.cellW / 2;
  const y1 = pos.localCy - pos.cellH / 2;
  const x2 = pos.localCx + pos.cellW / 2;
  const y2 = pos.localCy + pos.cellH / 2;
  const defs = [
    { key: 'T', coords: [x1, y1, x2, y1] },
    { key: 'B', coords: [x1, y2, x2, y2] },
    { key: 'L', coords: [x1, y1, x1, y2] },
    { key: 'R', coords: [x2, y1, x2, y2] },
  ];
  const lines = [];
  for (const { key, coords } of defs) {
    const b = borders && borders[key];
    const color = b ? (b.color || fallbackColor) : fallbackColor;
    const width = b ? (b.width ?? fallbackWidth) : fallbackWidth;
    if (width > 0) {
      const strokeColor = typeof color === 'string' && color.startsWith('#') ? color : '#' + color;
      lines.push(new fabric.Line(coords, {
        stroke: strokeColor,
        strokeWidth: width,
        selectable: false, evented: false,
        _isTableBorder: true,
        _cellRow: row, _cellCol: col, _borderDir: key,
      }));
    }
  }
  return lines;
};

/**
 * 셀 위치를 그룹 로컬 좌표로 계산
 * @param {number} row
 * @param {number} col
 * @param {number[]} colWidths
 * @param {number[]} rowHeights
 * @param {number} gridSpan
 * @param {number} rowSpan
 * @returns {{ localCx: number, localCy: number, cellW: number, cellH: number }}
 */
export const calcCellPosition = (row, col, colWidths, rowHeights, gridSpan = 1, rowSpan = 1) => {
  const tableW = colWidths.reduce((a, b) => a + b, 0);
  const tableH = rowHeights.reduce((a, b) => a + b, 0);
  const cellX = colWidths.slice(0, col).reduce((a, b) => a + b, 0);
  const cellY = rowHeights.slice(0, row).reduce((a, b) => a + b, 0);
  const cellW = colWidths.slice(col, col + gridSpan).reduce((a, b) => a + b, 0);
  const cellH = rowHeights.slice(row, row + rowSpan).reduce((a, b) => a + b, 0);
  return {
    localCx: cellX + cellW / 2 - tableW / 2,
    localCy: cellY + cellH / 2 - tableH / 2,
    cellW, cellH,
  };
};

/**
 * 다중 셀 하이라이트 (선택된 셀에 파란 테두리 표시)
 * @param {fabric.Group} tableGroup
 * @param {Array<{row:number, col:number}>} selectedCells
 */
export const highlightTableCells = (tableGroup, selectedCells) => {
  const cellSet = new Set(selectedCells.map(c => `${c.row}_${c.col}`));
  const isMulti = selectedCells.length > 1;
  const children = tableGroup.getObjects ? tableGroup.getObjects() : [];
  children.forEach(child => {
    if (!child._isTableCell) return;
    const isSelected = cellSet.has(`${child._cellRow}_${child._cellCol}`);
    if (isSelected) {
      if (child._origFill === undefined) {
        child._origFill = child.fill;
      }
      child._hlActive = true;
      // 셀 Rect에 파란 테두리 + 다중 선택 시 반투명 배경
      child.set({
        stroke: '#3b82f6',
        strokeWidth: 2.5,
        fill: isMulti ? 'rgba(0, 0, 0, 0.2)' : (child._origFill !== undefined ? child._origFill : child.fill),
      });
    } else {
      // 하이라이트 해제: 셀 Rect는 stroke 없음 (테두리는 Line 객체)
      if (child._hlActive) {
        child.set({ stroke: null, strokeWidth: 0 });
        delete child._hlActive;
      }
      if (child._origFill !== undefined) {
        child.set({ fill: child._origFill });
        delete child._origFill;
      }
    }
  });
};

/**
 * 단일 셀 하이라이트 (호환용)
 */
export const highlightTableCell = (tableGroup, row, col) => {
  highlightTableCells(tableGroup, [{ row, col }]);
};

/**
 * 테이블의 모든 셀 하이라이트 제거
 * @param {fabric.Group} tableGroup
 */
export const clearTableCellHighlight = (tableGroup) => {
  const children = tableGroup.getObjects ? tableGroup.getObjects() : [];
  children.forEach(child => {
    if (!child._isTableCell) return;
    if (child._hlActive) {
      child.set({ stroke: null, strokeWidth: 0 });
      delete child._hlActive;
    }
    if (child._origFill !== undefined) {
      child.set({ fill: child._origFill });
      delete child._origFill;
    }
  });
};

/**
 * 마우스 이벤트로 테이블 내 셀 위치 계산 (히트 테스트)
 * @param {fabric.Group} tableGroup
 * @param {Object} e - fabric 이벤트 객체
 * @param {fabric.Canvas} canvas
 * @returns {{ row: number, col: number } | null}
 */
export const hitTestTableCell = (tableGroup, e, canvas) => {
  const z = canvas.getZoom ? canvas.getZoom() : 1;
  const pointer = e.scenePoint || { x: e.e.offsetX / z, y: e.e.offsetY / z };
  const groupCenter = tableGroup.getCenterPoint();
  const scaleX = tableGroup.scaleX || 1;
  const scaleY = tableGroup.scaleY || 1;
  const localX = (pointer.x - groupCenter.x) / scaleX;
  const localY = (pointer.y - groupCenter.y) / scaleY;

  for (const rect of tableGroup.getObjects().filter(c => c._isTableCell)) {
    const hw = (rect.width || 0) / 2;
    const hh = (rect.height || 0) / 2;
    if (localX >= rect.left - hw && localX <= rect.left + hw &&
        localY >= rect.top - hh && localY <= rect.top + hh) {
      return { row: rect._cellRow, col: rect._cellCol };
    }
  }
  return null;
};

/**
 * 선택 영역을 사각형으로 확장
 * @param {{ row: number, col: number }} firstCell
 * @param {{ row: number, col: number }} lastCell
 * @returns {Array<{row:number, col:number}>}
 */
export const expandCellSelection = (firstCell, lastCell) => {
  const minRow = Math.min(firstCell.row, lastCell.row);
  const maxRow = Math.max(firstCell.row, lastCell.row);
  const minCol = Math.min(firstCell.col, lastCell.col);
  const maxCol = Math.max(firstCell.col, lastCell.col);
  const cells = [];
  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minCol; c <= maxCol; c++) {
      cells.push({ row: r, col: c });
    }
  }
  return cells;
};

/**
 * 테이블 편집 모드 진입 시 공통 설정
 * @param {fabric.Group} tableGroup
 */
export const enterTableEditMode = (tableGroup) => {
  tableGroup.set({
    subTargetCheck: true, interactive: true,
    hasBorders: false, hasControls: false,
    lockMovementX: true, lockMovementY: true,
  });
  tableGroup.setCoords();
  tableGroup.getObjects().forEach(child => {
    if (child.setCoords) child.setCoords();
    if (child._isTableCellText) {
      child.set({
        hasBorders: false, hasControls: false,
        lockMovementX: true, lockMovementY: true,
      });
    }
  });
};

/**
 * 빈 테이블 생성
 */
export const createEmptyTable = (rows, cols, canvasSize, options = {}) => {
  const {
    borderColor = '#000000',
    borderWidth = 1,
  } = options;

  const maxW = canvasSize.width * 0.8;
  const maxH = canvasSize.height * 0.6;
  const cellWidth = Math.min(150, Math.floor(maxW / cols));
  const cellHeight = Math.min(40, Math.floor(maxH / rows));
  const colWidths = Array(cols).fill(cellWidth);
  const rowHeights = Array(rows).fill(cellHeight);
  const cx = canvasSize.width / 2;
  const cy = canvasSize.height / 2;
  const marL = DEFAULT_TEXT_INSET.L, marR = DEFAULT_TEXT_INSET.R;
  const marT = DEFAULT_TEXT_INSET.T, marB = DEFAULT_TEXT_INSET.B;

  const defaultBorders = {
    T: { color: borderColor, width: borderWidth },
    B: { color: borderColor, width: borderWidth },
    L: { color: borderColor, width: borderWidth },
    R: { color: borderColor, width: borderWidth },
  };

  const fabricObjects = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const pos = calcCellPosition(r, c, colWidths, rowHeights);
      const textOffX = (marL - marR) / 2;

      fabricObjects.push(new fabric.Rect({
        width: pos.cellW, height: pos.cellH,
        fill: 'transparent',
        stroke: null, strokeWidth: 0,
        left: pos.localCx, top: pos.localCy,
        originX: 'center', originY: 'center',
        selectable: false, evented: false,
        _cellRow: r, _cellCol: c, _isTableCell: true,
        _gridSpan: 1, _rowSpan: 1,
        _marL: marL, _marR: marR, _marT: marT, _marB: marB,
        _borders: { ...defaultBorders },
        _origStroke: borderColor, _origStrokeWidth: borderWidth,
      }));

      // 4방향 테두리 Line
      const borderLines = createCellBorderLines(r, c, pos, defaultBorders);
      fabricObjects.push(...borderLines);

      fabricObjects.push(new fabric.Textbox('', {
        width: pos.cellW - marL - marR,
        left: pos.localCx + textOffX, top: pos.localCy, // 세로 중앙정렬
        originX: 'center', originY: 'center',
        fontFamily: 'Noto Sans KR', fontSize: 14, fill: '#000000',
        splitByGrapheme: true, wordWrap: true,
        selectable: true, evented: true,
        _cellRow: r, _cellCol: c, _isTableCellText: true,
        _gridSpan: 1, _rowSpan: 1,
      }));
    }
  }

  const group = new fabric.Group(fabricObjects, {
    left: cx, top: cy, originX: 'center', originY: 'center',
    id: `table-${Date.now()}`,
    name: 'Table',
    subTargetCheck: false, interactive: false,
    _isTableGroup: true,
    _tableRows: rows, _tableCols: cols,
    _tableColWidths: colWidths, _tableRowHeights: rowHeights,
  });
  setNoopLayoutManager(group);
  // FitContentLayout이 시프트한 자식 위치를 올바르게 재배치
  recalcTableCells(group);
  return group;
};

/**
 * 행 높이를 텍스트 내용에 맞게 자동 조절
 * @param {fabric.Group} tableGroup
 * @param {number} editedRow - 편집 중인 행 인덱스 (-1이면 증가만)
 * @returns {boolean} 변경 여부
 */
/**
 * Fabric.js Textbox의 실제 텍스트 높이 계산
 * (trailing newline이 calcTextHeight에 반영되지 않는 문제 보정)
 */
export const getActualTextHeight = (textObj) => {
  let h = textObj.calcTextHeight ? textObj.calcTextHeight() : (textObj.height || 0);
  // Fabric.js Textbox: 끝에 \n이 있으면 빈 줄이 _textLines에 포함되지 않음 → 보정
  const str = textObj.text || '';
  const trailingMatch = str.match(/\n+$/);
  if (trailingMatch && textObj.getHeightOfLine) {
    const extraLines = trailingMatch[0].length;
    const lineH = textObj.getHeightOfLine(0);
    h += extraLines * lineH;
  }
  return h;
};

export const autoFitRowHeights = (tableGroup, editedRow = -1, onlyExpand = false) => {
  const editedRows = editedRow === -1
    ? new Set()
    : (Array.isArray(editedRow) ? new Set(editedRow) : new Set([editedRow]));

  const rowHeights = tableGroup._tableRowHeights;
  const rows = tableGroup._tableRows;
  const texts = tableGroup.getObjects().filter(c => c._isTableCellText);
  const rects = tableGroup.getObjects().filter(c => c._isTableCell);
  const MIN_ROW_HEIGHT = TABLE_MIN_ROW_HEIGHT;

  // 텍스트 치수 강제 재계산 — 대상 행만 (편집 중인 텍스트는 이미 계산됨 → 건너뜀)
  for (const text of texts) {
    if (editedRows.size === 0 || editedRows.has(text._cellRow)) {
      if (text.initDimensions && !text.isEditing) text.initDimensions();
    }
  }

  const neededHeights = [...rowHeights];

  for (let r = 0; r < rows; r++) {
    if (editedRows.size > 0 && !editedRows.has(r)) continue;

    // 각 셀의 (marT + textH + marB) 중 최댓값
    let maxNeeded = MIN_ROW_HEIGHT;
    for (const text of texts) {
      if (text._cellRow !== r || (text._rowSpan || 1) > 1) continue;
      const rect = rects.find(rc => rc._cellRow === r && rc._cellCol === text._cellCol);
      const marT = rect?._marT ?? DEFAULT_TEXT_INSET.T;
      const marB = rect?._marB ?? DEFAULT_TEXT_INSET.B;
      const textH = getActualTextHeight(text);
      maxNeeded = Math.max(maxNeeded, textH + marT + marB);
    }
    neededHeights[r] = onlyExpand ? Math.max(maxNeeded, rowHeights[r]) : maxNeeded;
  }

  // rowSpan > 1 셀: 전체 모드에서만 처리
  if (editedRows.size === 0) {
    for (const text of texts) {
      const rs = text._rowSpan || 1;
      if (rs <= 1) continue;
      const r = text._cellRow;
      const rect = rects.find(rc => rc._cellRow === r && rc._cellCol === text._cellCol);
      const marT = rect?._marT ?? DEFAULT_TEXT_INSET.T;
      const marB = rect?._marB ?? DEFAULT_TEXT_INSET.B;
      const textH = getActualTextHeight(text);
      const needed = textH + marT + marB;
      const spanH = neededHeights.slice(r, r + rs).reduce((a, b) => a + b, 0);
      if (needed > spanH) {
        neededHeights[r + rs - 1] += (needed - spanH);
      }
    }
  }

  let changed = false;
  for (let i = 0; i < rows; i++) {
    if (Math.abs(neededHeights[i] - rowHeights[i]) > 0.5) {
      changed = true;
      break;
    }
  }
  if (!changed) return false;

  tableGroup._tableRowHeights = neededHeights;
  recalcTableCells(tableGroup);
  return true;
};

/**
 * 테이블 셀 위치 전체 재계산 (내부 border remove/add drift 자동 보정)
 * - 경계선 드래그 등 직접 호출 시: 내부 save/restore로 위치 보존
 * - 구조 변경(행/열 추가·삭제, 병합) 후에는 recalcTableLayout() 사용 권장
 */
export const recalcTableCells = (tableGroup) => {
  const savedLeft = tableGroup.left;
  const savedTop = tableGroup.top;
  const colWidths = tableGroup._tableColWidths;
  const rowHeights = tableGroup._tableRowHeights;
  const tableW = colWidths.reduce((a, b) => a + b, 0);
  const tableH = rowHeights.reduce((a, b) => a + b, 0);
  const rects = tableGroup.getObjects().filter(c => c._isTableCell);
  const texts = tableGroup.getObjects().filter(c => c._isTableCellText);

  for (const rect of rects) {
    const pos = calcCellPosition(rect._cellRow, rect._cellCol, colWidths, rowHeights, rect._gridSpan || 1, rect._rowSpan || 1);
    rect.set({ width: pos.cellW, height: pos.cellH, left: pos.localCx, top: pos.localCy });
  }

  for (const text of texts) {
    const rect = rects.find(r => r._cellRow === text._cellRow && r._cellCol === text._cellCol);
    const marL = rect?._marL ?? DEFAULT_TEXT_INSET.L, marR = rect?._marR ?? DEFAULT_TEXT_INSET.R;
    const pos = calcCellPosition(text._cellRow, text._cellCol, colWidths, rowHeights, text._gridSpan || 1, text._rowSpan || 1);
    const newW = pos.cellW - marL - marR;
    const newL = pos.localCx + (marL - marR) / 2;
    const newT = pos.localCy;

    if (text.isEditing) {
      // 편집 중인 텍스트: set() 대신 직접 할당 → textarea 재생성 방지
      // top 변경 금지: Fabric.js의 updateFromTextArea() → setPositionByOrigin()이
      // textHeight 기반으로 top을 설정하므로, 우리가 cellCenterY로 덮어쓰면
      // 커서 위치가 어긋남. left/width만 업데이트.
      if (Math.abs(text.width - newW) > 0.5 || Math.abs(text.left - newL) > 0.5) {
        text.left = newL;
        text.width = newW;
      }
      // top은 변경하지 않음 — 편집 종료 후 recalcTableCells에서 보정됨
    } else {
      text.set({ width: newW, left: newL, top: newT });
    }
  }

  // Border Lines 재생성 + 고아 객체 정리 — FitContentLayout 억제
  const origLM = tableGroup.layoutManager;
  tableGroup.layoutManager = NOOP_LAYOUT_MANAGER;

  // 기존 보더 라인만 제거 (보더 + 고아 객체)
  const allChildren = tableGroup.getObjects();
  const toRemove = allChildren.filter(c =>
    c._isTableBorder || c._isMarginDebug ||
    (!c._isTableCell && !c._isTableCellText && !c._isTableBorder)
  );

  // Fabric.js v7 Group.remove()는 _exitGroup()으로 좌표 변환을 수행하므로
  // 내부 _objects 배열에서 직접 제거하여 부작용 방지
  const groupObjects = tableGroup._objects;
  for (let i = groupObjects.length - 1; i >= 0; i--) {
    if (toRemove.includes(groupObjects[i])) {
      const c = groupObjects[i];
      groupObjects.splice(i, 1);
      c.group = undefined;
      c.canvas = undefined;
      c.parent = undefined;
    }
  }

  // Fabric.js v7 Group.add()는 enterGroup(obj, true)로 캔버스→로컬 좌표 변환을 수행하므로
  // 이미 로컬 좌표인 border line은 _objects에 직접 추가하여 이중 변환 방지
  for (const rect of rects) {
    const pos = calcCellPosition(rect._cellRow, rect._cellCol, colWidths, rowHeights, rect._gridSpan || 1, rect._rowSpan || 1);
    const lines = createCellBorderLines(rect._cellRow, rect._cellCol, pos, rect._borders, rect._origStroke, rect._origStrokeWidth);
    for (const l of lines) {
      groupObjects.push(l);
      l.group = tableGroup;
      l.parent = tableGroup;
      l.canvas = tableGroup.canvas;
    }
  }
  tableGroup._set('dirty', true);

  tableGroup.layoutManager = origLM;

  tableGroup.set({ width: tableW, height: tableH, left: savedLeft, top: savedTop });
  tableGroup.setCoords();
};

/**
 * 테이블 구조 변경 후 전체 레이아웃 재계산 + 위치 복원
 * - add()/remove()로 그룹 자식이 변경된 후 호출
 * - savedPos: 구조 변경 전에 저장한 { left, top }
 */
export const recalcTableLayout = (tableGroup, savedPos, affectedRows) => {
  recalcTableCells(tableGroup);
  if (affectedRows && affectedRows.length > 0) {
    const oldH = tableGroup._tableRowHeights.reduce((a, b) => a + b, 0);
    autoFitRowHeights(tableGroup, affectedRows);
    const newH = tableGroup._tableRowHeights.reduce((a, b) => a + b, 0);
    const scaleY = tableGroup.scaleY || 1;
    savedPos.top += (newH - oldH) * scaleY / 2;
  }
  tableGroup.set({ left: savedPos.left, top: savedPos.top });
  tableGroup.setCoords();
};

/**
 * 행 추가 (afterRowIndex 뒤에 삽입)
 */
export const addTableRow = (tableGroup, afterRowIndex) => {
  const cols = tableGroup._tableCols;
  const colWidths = tableGroup._tableColWidths;
  const rowHeights = [...tableGroup._tableRowHeights];
  const newHeight = rowHeights[afterRowIndex] || 40;
  rowHeights.splice(afterRowIndex + 1, 0, newHeight);
  tableGroup._tableRowHeights = rowHeights;
  tableGroup._tableRows += 1;

  tableGroup.getObjects().forEach(child => {
    if ((child._isTableCell || child._isTableCellText || child._isTableBorder) && child._cellRow > afterRowIndex) {
      child._cellRow += 1;
    }
  });

  const isEditMode = tableGroup.interactive === true;
  const allObjs = tableGroup.getObjects();

  const savedPos = { left: tableGroup.left, top: tableGroup.top + newHeight / 2 };

  // FitContentLayout 억제: add()가 자식 위치를 오염시키는 것 방지
  const origLM = tableGroup.layoutManager;
  tableGroup.layoutManager = NOOP_LAYOUT_MANAGER;

  for (let c = 0; c < cols; c++) {
    // 기준 행의 해당 열 셀에서 스타일 복사
    const refRect = allObjs.find(ch => ch._isTableCell && ch._cellRow === afterRowIndex && ch._cellCol === c);
    const refText = allObjs.find(ch => ch._isTableCellText && ch._cellRow === afterRowIndex && ch._cellCol === c);

    const cellFill = refRect?._origFill ?? refRect?.fill ?? 'transparent';
    const cellMarL = refRect?._marL ?? DEFAULT_TEXT_INSET.L;
    const cellMarR = refRect?._marR ?? DEFAULT_TEXT_INSET.R;
    const cellMarT = refRect?._marT ?? DEFAULT_TEXT_INSET.T;
    const cellMarB = refRect?._marB ?? DEFAULT_TEXT_INSET.B;
    const bdrColor = refRect?._origStroke || '#000000';
    const bdrWidth = refRect?._origStrokeWidth || 1;
    const cellBorders = refRect?._borders
      ? JSON.parse(JSON.stringify(refRect._borders))
      : { T: { color: bdrColor, width: bdrWidth }, B: { color: bdrColor, width: bdrWidth },
          L: { color: bdrColor, width: bdrWidth }, R: { color: bdrColor, width: bdrWidth } };

    const pos = calcCellPosition(afterRowIndex + 1, c, colWidths, rowHeights);
    // Fabric.js v7 Group.add()의 enterGroup(true) 좌표 이중 변환 방지 → 직접 추가
    const newRect = new fabric.Rect({
      width: pos.cellW, height: pos.cellH, fill: cellFill,
      stroke: null, strokeWidth: 0,
      left: pos.localCx, top: pos.localCy,
      originX: 'center', originY: 'center',
      selectable: false, evented: false,
      _cellRow: afterRowIndex + 1, _cellCol: c, _isTableCell: true,
      _gridSpan: 1, _rowSpan: 1,
      _marL: cellMarL, _marR: cellMarR, _marT: cellMarT, _marB: cellMarB,
      _borders: cellBorders,
      _origStroke: bdrColor, _origStrokeWidth: bdrWidth,
    });
    const newText = new fabric.Textbox('', {
      width: pos.cellW - cellMarL - cellMarR,
      left: pos.localCx + (cellMarL - cellMarR) / 2, top: pos.localCy,
      originX: 'center', originY: 'center',
      fontFamily: refText?.fontFamily || 'Noto Sans KR',
      fontSize: refText?.fontSize || 14,
      fill: refText?.fill || '#000000',
      fontWeight: refText?.fontWeight || 'normal',
      fontStyle: refText?.fontStyle || 'normal',
      textAlign: refText?.textAlign || 'left',
      underline: refText?.underline || false,
      linethrough: refText?.linethrough || false,
      splitByGrapheme: true, wordWrap: true,
      selectable: true, evented: true,
      hasBorders: !isEditMode, hasControls: !isEditMode,
      lockMovementX: isEditMode, lockMovementY: isEditMode,
      _cellRow: afterRowIndex + 1, _cellCol: c, _isTableCellText: true,
      _gridSpan: 1, _rowSpan: 1,
    });
    for (const obj of [newRect, newText]) {
      tableGroup._objects.push(obj);
      obj.group = tableGroup;
      obj.parent = tableGroup;
      obj.canvas = tableGroup.canvas;
    }
  }

  tableGroup.layoutManager = origLM;
  recalcTableLayout(tableGroup, savedPos);
};

/**
 * 행 삭제
 */
export const deleteTableRow = (tableGroup, rowIndex) => {
  if (tableGroup._tableRows <= 1) return false;
  const deletedHeight = tableGroup._tableRowHeights[rowIndex];
  const rowHeights = [...tableGroup._tableRowHeights];
  rowHeights.splice(rowIndex, 1);
  tableGroup._tableRowHeights = rowHeights;
  tableGroup._tableRows -= 1;

  const savedPos = { left: tableGroup.left, top: tableGroup.top - deletedHeight / 2 };

  // FitContentLayout 억제: remove()가 자식 위치를 오염시키는 것 방지
  const origLM = tableGroup.layoutManager;
  tableGroup.layoutManager = NOOP_LAYOUT_MANAGER;

  // Fabric.js v7 Group.remove()의 _exitGroup 좌표 변환 방지 → 직접 제거
  const rowToRemove = tableGroup.getObjects().filter(
    c => (c._isTableCell || c._isTableCellText || c._isTableBorder) && c._cellRow === rowIndex
  );
  const objs = tableGroup._objects;
  for (let i = objs.length - 1; i >= 0; i--) {
    if (rowToRemove.includes(objs[i])) {
      const c = objs[i];
      objs.splice(i, 1);
      c.group = undefined;
      c.canvas = undefined;
      c.parent = undefined;
    }
  }

  tableGroup.layoutManager = origLM;

  tableGroup.getObjects().forEach(child => {
    if ((child._isTableCell || child._isTableCellText || child._isTableBorder) && child._cellRow > rowIndex) {
      child._cellRow -= 1;
    }
  });
  recalcTableLayout(tableGroup, savedPos);
  return true;
};

/**
 * 열 추가 (afterColIndex 뒤에 삽입)
 */
export const addTableColumn = (tableGroup, afterColIndex) => {
  const rows = tableGroup._tableRows;
  const colWidths = [...tableGroup._tableColWidths];
  const rowHeights = tableGroup._tableRowHeights;
  const newWidth = colWidths[afterColIndex] || 120;
  colWidths.splice(afterColIndex + 1, 0, newWidth);
  tableGroup._tableColWidths = colWidths;
  tableGroup._tableCols += 1;

  tableGroup.getObjects().forEach(child => {
    if ((child._isTableCell || child._isTableCellText || child._isTableBorder) && child._cellCol > afterColIndex) {
      child._cellCol += 1;
    }
  });

  const isEditMode = tableGroup.interactive === true;
  const allObjs = tableGroup.getObjects();

  const savedPos = { left: tableGroup.left + newWidth / 2, top: tableGroup.top };

  // FitContentLayout 억제: add()가 자식 위치를 오염시키는 것 방지
  const origLM = tableGroup.layoutManager;
  tableGroup.layoutManager = NOOP_LAYOUT_MANAGER;

  for (let r = 0; r < rows; r++) {
    // 기준 열의 해당 행 셀에서 스타일 복사
    const refRect = allObjs.find(ch => ch._isTableCell && ch._cellRow === r && ch._cellCol === afterColIndex);
    const refText = allObjs.find(ch => ch._isTableCellText && ch._cellRow === r && ch._cellCol === afterColIndex);

    const cellFill = refRect?._origFill ?? refRect?.fill ?? 'transparent';
    const cellMarL = refRect?._marL ?? DEFAULT_TEXT_INSET.L;
    const cellMarR = refRect?._marR ?? DEFAULT_TEXT_INSET.R;
    const cellMarT = refRect?._marT ?? DEFAULT_TEXT_INSET.T;
    const cellMarB = refRect?._marB ?? DEFAULT_TEXT_INSET.B;
    const bdrColor = refRect?._origStroke || '#000000';
    const bdrWidth = refRect?._origStrokeWidth || 1;
    const cellBorders = refRect?._borders
      ? JSON.parse(JSON.stringify(refRect._borders))
      : { T: { color: bdrColor, width: bdrWidth }, B: { color: bdrColor, width: bdrWidth },
          L: { color: bdrColor, width: bdrWidth }, R: { color: bdrColor, width: bdrWidth } };

    const pos = calcCellPosition(r, afterColIndex + 1, colWidths, rowHeights);
    // Fabric.js v7 Group.add()의 enterGroup(true) 좌표 이중 변환 방지 → 직접 추가
    const newRect = new fabric.Rect({
      width: pos.cellW, height: pos.cellH, fill: cellFill,
      stroke: null, strokeWidth: 0,
      left: pos.localCx, top: pos.localCy,
      originX: 'center', originY: 'center',
      selectable: false, evented: false,
      _cellRow: r, _cellCol: afterColIndex + 1, _isTableCell: true,
      _gridSpan: 1, _rowSpan: 1,
      _marL: cellMarL, _marR: cellMarR, _marT: cellMarT, _marB: cellMarB,
      _borders: cellBorders,
      _origStroke: bdrColor, _origStrokeWidth: bdrWidth,
    });
    const newText = new fabric.Textbox('', {
      width: pos.cellW - cellMarL - cellMarR,
      left: pos.localCx + (cellMarL - cellMarR) / 2, top: pos.localCy,
      originX: 'center', originY: 'center',
      fontFamily: refText?.fontFamily || 'Noto Sans KR',
      fontSize: refText?.fontSize || 14,
      fill: refText?.fill || '#000000',
      fontWeight: refText?.fontWeight || 'normal',
      fontStyle: refText?.fontStyle || 'normal',
      textAlign: refText?.textAlign || 'left',
      underline: refText?.underline || false,
      linethrough: refText?.linethrough || false,
      splitByGrapheme: true, wordWrap: true,
      selectable: true, evented: true,
      hasBorders: !isEditMode, hasControls: !isEditMode,
      lockMovementX: isEditMode, lockMovementY: isEditMode,
      _cellRow: r, _cellCol: afterColIndex + 1, _isTableCellText: true,
      _gridSpan: 1, _rowSpan: 1,
    });
    for (const obj of [newRect, newText]) {
      tableGroup._objects.push(obj);
      obj.group = tableGroup;
      obj.parent = tableGroup;
      obj.canvas = tableGroup.canvas;
    }
  }

  tableGroup.layoutManager = origLM;
  recalcTableLayout(tableGroup, savedPos);
};

/**
 * 열 삭제
 */
export const deleteTableColumn = (tableGroup, colIndex) => {
  if (tableGroup._tableCols <= 1) return false;
  const deletedWidth = tableGroup._tableColWidths[colIndex];
  const colWidths = [...tableGroup._tableColWidths];
  colWidths.splice(colIndex, 1);
  tableGroup._tableColWidths = colWidths;
  tableGroup._tableCols -= 1;

  const savedPos = { left: tableGroup.left - deletedWidth / 2, top: tableGroup.top };

  // FitContentLayout 억제: remove()가 자식 위치를 오염시키는 것 방지
  const origLM = tableGroup.layoutManager;
  tableGroup.layoutManager = NOOP_LAYOUT_MANAGER;

  // Fabric.js v7 Group.remove()의 _exitGroup 좌표 변환 방지 → 직접 제거
  const colToRemove = tableGroup.getObjects().filter(
    c => (c._isTableCell || c._isTableCellText || c._isTableBorder) && c._cellCol === colIndex
  );
  const colObjs = tableGroup._objects;
  for (let i = colObjs.length - 1; i >= 0; i--) {
    if (colToRemove.includes(colObjs[i])) {
      const c = colObjs[i];
      colObjs.splice(i, 1);
      c.group = undefined;
      c.canvas = undefined;
      c.parent = undefined;
    }
  }

  tableGroup.layoutManager = origLM;

  tableGroup.getObjects().forEach(child => {
    if ((child._isTableCell || child._isTableCellText || child._isTableBorder) && child._cellCol > colIndex) {
      child._cellCol -= 1;
    }
  });
  recalcTableLayout(tableGroup, savedPos);
  return true;
};

/**
 * 경계선 히트 테스트 (행/열 경계선 감지)
 */
export const hitTestTableBorder = (tableGroup, e, canvas, threshold = 5) => {
  const z = canvas.getZoom ? canvas.getZoom() : 1;
  const pointer = e.scenePoint || { x: e.e.offsetX / z, y: e.e.offsetY / z };
  const groupCenter = tableGroup.getCenterPoint();
  const scaleX = tableGroup.scaleX || 1;
  const scaleY = tableGroup.scaleY || 1;
  const localX = (pointer.x - groupCenter.x) / scaleX;
  const localY = (pointer.y - groupCenter.y) / scaleY;
  const t = threshold / z;

  const colWidths = tableGroup._tableColWidths;
  const rowHeights = tableGroup._tableRowHeights;
  const tableW = colWidths.reduce((a, b) => a + b, 0);
  const tableH = rowHeights.reduce((a, b) => a + b, 0);

  // 내부 열 경계선
  let cumX = -tableW / 2;
  for (let i = 0; i < colWidths.length - 1; i++) {
    cumX += colWidths[i];
    if (Math.abs(localX - cumX) < t && localY >= -tableH / 2 - t && localY <= tableH / 2 + t) {
      return { type: 'col', index: i, localPos: localX };
    }
  }

  // 내부 행 경계선
  let cumY = -tableH / 2;
  for (let i = 0; i < rowHeights.length - 1; i++) {
    cumY += rowHeights[i];
    if (Math.abs(localY - cumY) < t && localX >= -tableW / 2 - t && localX <= tableW / 2 + t) {
      return { type: 'row', index: i, localPos: localY };
    }
  }

  // 외곽 테두리: 왼쪽 (첫 열 너비 조절)
  if (Math.abs(localX - (-tableW / 2)) < t && localY >= -tableH / 2 - t && localY <= tableH / 2 + t) {
    return { type: 'edge-left', index: 0, localPos: localX };
  }
  // 외곽 테두리: 오른쪽 (마지막 열 너비 조절)
  if (Math.abs(localX - (tableW / 2)) < t && localY >= -tableH / 2 - t && localY <= tableH / 2 + t) {
    return { type: 'edge-right', index: colWidths.length - 1, localPos: localX };
  }
  // 외곽 테두리: 위쪽 (첫 행 높이 조절)
  if (Math.abs(localY - (-tableH / 2)) < t && localX >= -tableW / 2 - t && localX <= tableW / 2 + t) {
    return { type: 'edge-top', index: 0, localPos: localY };
  }
  // 외곽 테두리: 아래쪽 (마지막 행 높이 조절)
  if (Math.abs(localY - (tableH / 2)) < t && localX >= -tableW / 2 - t && localX <= tableW / 2 + t) {
    return { type: 'edge-bottom', index: rowHeights.length - 1, localPos: localY };
  }

  return null;
};

/**
 * 테이블 전체 테두리 색상 변경
 */
export const setTableBorderColor = (tableGroup, color) => {
  tableGroup.getObjects().forEach(child => {
    if (child._isTableBorder) {
      child.set({ stroke: color });
    }
    if (child._isTableCell) {
      child._origStroke = color;
      if (child._borders) {
        for (const key of Object.keys(child._borders)) {
          if (child._borders[key]) child._borders[key].color = color;
        }
      }
    }
  });
};

/**
 * 테이블 전체 테두리 두께 변경
 */
export const setTableBorderWidth = (tableGroup, width) => {
  tableGroup.getObjects().forEach(child => {
    if (child._isTableBorder) {
      child.set({ strokeWidth: width, visible: width > 0 });
    }
    if (child._isTableCell) {
      child._origStrokeWidth = width;
      if (child._borders) {
        for (const key of Object.keys(child._borders)) {
          if (child._borders[key]) child._borders[key].width = width;
        }
      }
    }
  });
};
